import { RequestOptions } from '@algolia/transporter';
import { useState } from 'react';
import useSWRInfinite from 'swr/infinite';

import { emptyQuery } from './usePaginatedTemplatesSearch.config';
import { fetchTemplates, parseTemplates } from './usePaginatedTemplatesSearch.helpers';
import {
  PaginatedTemplateSearch,
  PaginatedTemplatesSearchOptions,
  SearchApiResponse,
  TemplateFilters,
  TemplateHit,
} from './usePaginatedTemplatesSearch.types';

/**
 *  Hook that returns Algolia template search functionality.
 *  Comes with retries and caching and pagination.
 *  The hook returns:
 *      - Functions for searching templates
 *      - The search state (e.g. loading, error)
 *      - The template search result
 * @returns {PaginatedTemplatesSearch}
 */
export const usePaginatedTemplatesSearch = (options?: PaginatedTemplatesSearchOptions): PaginatedTemplateSearch => {
  const [query, setQuery] = useState<string | null>(null);
  const [filters, setFilters] = useState<TemplateFilters>(options?.filters || {});

  // cache key for templates search query result per page and isAccessible filter option
  const getKey = (page: number) => {
    let key = `${query}_${page}_${!!filters.isAccessible}`;

    if (!!filters.isAccessible) {
      key += `_isAccessible`;
    }
    if (Array.isArray(filters?.planType) && filters.planType.length > 0) {
      key += `_${filters.planType.join('_')}`;
    }
    return key;
  };

  const fetchFn = fetchTemplates(filters, options?.useInfographTemplates);

  const swrConfig = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateFirstPage: false,
    revalidateOnMount: false,

    // skip fetch if there is no query and fetchAllByFilter is not set
    fallbackData: options?.fetchAllByFilter || query ? undefined : [emptyQuery],
  };

  const {
    data: initialData,
    error,
    size: page,
    setSize: setPage,
  } = useSWRInfinite<RequestOptions>(getKey, fetchFn, swrConfig);
  const data = initialData as SearchApiResponse[];

  const searchTemplates = (search: string) => {
    setQuery(search);
  };

  const clearSearch = () => {
    if (!query) return;

    setQuery('');
    setPage(1);
  };

  const onShowMore = () => {
    // Only increment page if no query is set
    // Otherwise, for search results a set amount of results will be displayed similar to E1 templates page
    if (!query) {
      setPage(page + 1);
    }
  };

  const templates: TemplateHit[] = parseTemplates(data, filters);
  const hasMore = data && data.length ? data[data.length - 1]?.nbPages - 1 > data[data.length - 1]?.page : false;
  const isLoadingInitialData = !data && !error;
  const isLoadingMore = !!(isLoadingInitialData || (page > 0 && data && typeof data[page - 1] === 'undefined'));
  const hasNoResultsFound = !!(templates.length === 0 && !isLoadingInitialData && query);

  const cleanedDuplicateTemplates = templates.filter(
    (template, index, self) => index === self.findIndex((t) => t.template_id === template.template_id),
  );

  return {
    error,
    hasMore,
    isLoadingMore,
    isLoadingInitialData,
    query: query || '',
    templates: cleanedDuplicateTemplates,
    onShowMore,
    clearSearch,
    searchTemplates,
    hasNoResultsFound,
    setFilters,
    filters,
  };
};
